import ArrowsPointingOutIcon from '@heroicons/react/24/outline/ArrowsPointingOutIcon';
import DocumentIcon from '@heroicons/react/24/outline/DocumentIcon';
import PencilIcon from '@heroicons/react/24/outline/PencilIcon';
import PhotoIcon from '@heroicons/react/24/outline/PhotoIcon';
import { useState } from 'react';

import { Button } from '@/components/core/Button';
import { ButtonPanel } from '@/components/core/ButtonPanel';
import { Icon } from '@/components/core/Icon';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/services/utils';

interface DamAssetPreviewProps {
    /** Renderable image URL; `undefined` shows the placeholder. */
    imageUrl: string | undefined;
    isImage: boolean;
    title: string;
    onFullscreen: () => void;
    onReplace: () => void;
}

/**
 * Preview of the asset detail (wireframe `7037:47908`) with fullscreen (`7037:48201`) and
 * "Change image" (`7052:44171`, icon-only on sm). The image keeps its aspect ratio (D-027).
 */
function DamAssetPreview({ imageUrl, isImage, title, onFullscreen, onReplace }: DamAssetPreviewProps) {
    const { t } = useTranslation();
    const [imageFailed, setImageFailed] = useState(false);
    const showImage = Boolean(imageUrl) && !imageFailed;

    return (
        <div
            className={cn(
                'tw-relative tw-flex tw-aspect-[4/3] tw-w-full tw-items-center tw-justify-center tw-overflow-hidden',
                'tw-rounded-lg tw-border tw-border-solid tw-border-tertiary tw-bg-fill-primary',
            )}
        >
            {showImage ? (
                <img src={imageUrl} alt={title} className="tw-h-full tw-w-full tw-object-contain" onError={() => setImageFailed(true)} />
            ) : (
                <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center" role="img" aria-label={t('DAM.CARD.NO_PREVIEW')}>
                    <Icon icon={isImage ? PhotoIcon : DocumentIcon} size="xl" variant="lighter-gray" />
                </div>
            )}
            <div className="tw-absolute tw-left-4 tw-right-4 tw-top-4 tw-flex tw-items-start tw-justify-between tw-gap-2">
                <ButtonPanel
                    isDisabled={!showImage}
                    icon={ArrowsPointingOutIcon}
                    tooltip={t('DAM.DETAIL.FULLSCREEN')}
                    aria-label={t('DAM.DETAIL.FULLSCREEN')}
                    onClick={onFullscreen}
                    className="tw-bg-fill-primary"
                />
                <Button variant="outline" hasIcon icon={PencilIcon} onClick={onReplace} className="tw-hidden tw-bg-fill-primary md:tw-flex">
                    {t('DAM.DETAIL.CHANGE_IMAGE')}
                </Button>
                <ButtonPanel
                    isDisabled={false}
                    icon={PencilIcon}
                    tooltip={t('DAM.DETAIL.CHANGE_IMAGE')}
                    aria-label={t('DAM.DETAIL.CHANGE_IMAGE')}
                    onClick={onReplace}
                    className="tw-bg-fill-primary md:tw-hidden"
                />
            </div>
        </div>
    );
}

export { DamAssetPreview };
export type { DamAssetPreviewProps };
